// Thin admin-panel shell. All tab bodies live in /pages/admin/*.jsx (split out
// in the Feb-2026 refactor — see /app/memory/PRD.md). Keep this file small:
// header/footer chrome, tab routing, and stat fetch. New tabs go in TABS and
// `renderTab()`.
import { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft, Search, Award, Gift, MessageSquare, CreditCard,
  Users as UsersIcon, ShieldCheck, Settings, Eye, Inbox, TrendingUp,
  Tag, Mail, Calendar, History, LogOut, KeyRound,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import ChangePasswordModal from "../auth/ChangePasswordModal";
import { Stat } from "./admin/_shared";

import { CardsTab } from "./admin/CardsTab";
import { BrowseTab } from "./admin/BrowseTab";
import { ProgramTab } from "./admin/ProgramTab";
import { ReportsTab } from "./admin/ReportsTab";
import { ProposedMethodsTab } from "./admin/ProposedMethodsTab";
import { SupportTab } from "./admin/SupportTab";
import { EconomicsTab } from "./admin/EconomicsTab";
import { DealsTab } from "./admin/DealsTab";
import { BroadcastTab } from "./admin/BroadcastTab";
import { SubscriptionsTab } from "./admin/SubscriptionsTab";
import { AuditTab } from "./admin/AuditTab";
import { UsersTab } from "./admin/UsersTab";
import { LockoutsTab } from "./admin/LockoutsTab";
import { SettingsTab } from "./admin/SettingsTab";
import { SharingTab } from "./admin/SharingTab";

const TABS = [
  { key: "cards",         label: "Cards",         icon: CreditCard,    accent: "#10B981", Comp: CardsTab,    props: (ctx) => ({ onChange: ctx.refreshStats, snippet: ctx.snippet, bookmarklet: ctx.bookmarklet }) },
  { key: "browse",        label: "Browse",        icon: Search,        accent: "#A78BFA", Comp: BrowseTab,   props: (ctx) => ({ onChange: ctx.refreshStats }) },
  { key: "sas",           label: "SAS Bonus",     icon: Award,         accent: "#6A9CFF", Comp: ProgramTab,  props: (ctx) => ({ onChange: ctx.refreshStats, program: "sas",   accent: "#6A9CFF", href: "https://onlineshopping.flysas.com/nb-NO/alle-butikker/1", snippet: ctx.sasSnippet,   programName: "SAS EuroBonus" }) },
  { key: "trumf",         label: "Trumf Bonus",   icon: Gift,          accent: "#C4262E", Comp: ProgramTab,  props: (ctx) => ({ onChange: ctx.refreshStats, program: "trumf", accent: "#C4262E", href: "https://trumfnetthandel.no/kategori",                       snippet: ctx.trumfSnippet, programName: "Trumf Netthandel" }) },
  { key: "reports",       label: "Reports",       icon: MessageSquare, accent: "#F59E0B", Comp: ReportsTab,  props: (ctx) => ({ onChange: ctx.refreshStats }) },
  { key: "proposed",      label: "Proposed",      icon: ShieldCheck,   accent: "#0B6CF5", Comp: ProposedMethodsTab },
  { key: "support",       label: "Support",       icon: Inbox,         accent: "#8B5CF6", Comp: SupportTab },
  { key: "economics",     label: "Economics",     icon: TrendingUp,    accent: "#10B981", Comp: EconomicsTab, ownerOnly: true },
  { key: "deals",         label: "Deals",         icon: Tag,           accent: "#FF8A5C", Comp: DealsTab },
  { key: "broadcast",     label: "Broadcast",     icon: Mail,          accent: "#0B6CF5", Comp: BroadcastTab, ownerOnly: true },
  { key: "subscriptions", label: "Subscriptions", icon: Calendar,      accent: "#FF8A5C", Comp: SubscriptionsTab },
  { key: "audit",         label: "Audit",         icon: History,       accent: "#F472B6", Comp: AuditTab },
  { key: "users",         label: "Users",         icon: UsersIcon,     accent: "#22D3EE", Comp: UsersTab,    ownerOnly: true, props: (ctx) => ({ onChange: ctx.refreshStats }) },
  { key: "lockouts",      label: "Lockouts",      icon: ShieldCheck,   accent: "#EF4444", Comp: LockoutsTab, ownerOnly: true },
  { key: "settings",      label: "Settings",      icon: Settings,      accent: "#A78BFA", Comp: SettingsTab, ownerOnly: true },
  { key: "sharing",       label: "Sharing",       icon: Eye,           accent: "#06B6D4", Comp: SharingTab,  ownerOnly: true },
];

export default function AmexImport({ bookmarklet, snippet, trumfSnippet, sasSnippet }) {
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const initialTab = params.get("tab") || "cards";
  const [tab, setTab] = useState(TABS.find((t) => t.key === initialTab) ? initialTab : "cards");
  const [stats, setStats] = useState(null);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const visibleTabs = TABS.filter((t) => !t.ownerOnly || user?.role === "owner");

  const refreshStats = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/amex/stats`).then((r) => r.json()).then(setStats).catch(() => {});
  }, []);
  useEffect(() => { refreshStats(); }, [refreshStats]);
  // If user lost owner role, snap off owner-only tabs.
  useEffect(() => {
    const isOwnerOnly = TABS.find((t) => t.key === tab)?.ownerOnly;
    if (isOwnerOnly && user?.role !== "owner") setTab("cards");
  }, [tab, user]);

  const mustChange = !!user?.must_change_password;

  return (
    <div className="srs min-h-screen bg-[var(--srs-bg)] text-[var(--srs-ink)]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-50"
             style={{ background: "radial-gradient(closest-side, color-mix(in srgb, var(--srs-sun) 18%, transparent), transparent 70%)" }} />
      </div>

      <nav className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-6 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.25em] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> BACK
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[var(--srs-line)] bg-[var(--srs-surface)]" data-testid="user-pill">
            <span
              className="px-1.5 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
              style={{
                background: user?.role === "owner" ? "rgba(34,211,238,0.12)" : "rgba(16,185,129,0.10)",
                color: user?.role === "owner" ? "#22D3EE" : "#10B981",
                border: `1px solid ${user?.role === "owner" ? "rgba(34,211,238,0.35)" : "rgba(16,185,129,0.30)"}`,
              }}
              data-testid="user-role"
            >{user?.role || "—"}</span>
            <span className="text-xs text-[var(--srs-ink)] max-w-[180px] truncate" data-testid="user-email">{user?.email}</span>
          </div>
          <button
            onClick={() => setShowChangePwd(true)}
            data-testid="change-pwd-btn"
            title="Change password"
            className="inline-flex items-center justify-center w-9 h-9 border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[#10B981] hover:border-[#10B981] transition-colors">
            <KeyRound className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            title="Sign out"
            className="inline-flex items-center justify-center w-9 h-9 border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[#EF4444] hover:border-[#EF4444] transition-colors">
            <LogOut className="w-3.5 h-3.5" />
          </button>
          <div className="hidden sm:flex items-center gap-3">
            <span className="srs-logo-dot" style={{ width: 22, height: 22 }} aria-hidden="true" />
            <span className="text-[11px] tracking-[0.28em] font-bold whitespace-nowrap text-[var(--srs-ink)]">EUROPILOT · ADMIN</span>
          </div>
        </div>
      </nav>

      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-8">
        <div className="text-[10px] tracking-[0.3em] text-[var(--srs-ink-faint)] font-bold mb-4">// ADMIN PANEL</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.02]">
          Manage the <span className="text-[var(--srs-sun)] italic font-medium">EuroPilot</span><br />database.
        </h1>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[var(--srs-surface-2)] border border-[var(--srs-line-soft)]" data-testid="admin-stats">
          <Stat label="MERCHANTS"     value={stats?.count ?? "—"}         accent="#10B981" />
          <Stat label="AMEX (DIRECT)" value={stats?.direct ?? "—"}        accent="#10B981" />
          <Stat label="TRUMF"         value={stats?.trumf ?? "—"}         accent="#C4262E" />
          <Stat label="SAS"           value={stats?.sas ?? "—"}           accent="#6A9CFF" />
          <Stat label="OPEN REPORTS"  value={stats?.open_reports ?? "—"}  accent="#F59E0B" />
        </div>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pb-4">
        <div className="flex flex-wrap gap-px bg-[var(--srs-surface-2)] border border-[var(--srs-line-soft)]" data-testid="admin-tabs">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            const showBadge = t.key === "reports" && (stats?.open_reports ?? 0) > 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`relative flex-1 min-w-[120px] flex items-center justify-center gap-2 px-3 py-3 text-[11px] tracking-[0.2em] font-bold uppercase transition-colors ${active ? "text-[var(--srs-ink)]" : "text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]"}`}
                style={{
                  background: active ? "var(--srs-surface)" : "var(--srs-bg)",
                  borderBottom: active ? `2px solid ${t.accent}` : "2px solid transparent",
                }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: t.accent }} />
                {t.label}
                {showBadge && (
                  <span
                    className="absolute top-2 right-3 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-black"
                    style={{ background: t.accent }}
                    data-testid="reports-badge"
                  >
                    {stats.open_reports}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        {(() => {
          const meta = TABS.find((t) => t.key === tab);
          if (!meta) return null;
          if (meta.ownerOnly && user?.role !== "owner") return null;
          const Comp = meta.Comp;
          const ctx = { refreshStats, snippet, bookmarklet, sasSnippet, trumfSnippet };
          const props = meta.props ? meta.props(ctx) : {};
          return <Comp {...props} />;
        })()}
      </section>

      {(showChangePwd || mustChange) && (
        <ChangePasswordModal
          forced={mustChange}
          onClose={() => setShowChangePwd(false)}
          onDone={() => setShowChangePwd(false)}
        />
      )}
    </div>
  );
}
